import { createContext, useContext, useState, ReactNode, useMemo, useEffect, useRef, useCallback, SetStateAction } from 'react';
import { AIProcessLogItem } from '../../../services/query/AIProcessLogQuery';
import { parseImageUrls, createPhotoFilesFromHistory, fetchImageAsBase64, createEmptyHangerCardConfig, resolveHangerCardModelSelection, resolveDuplicateBarcode } from '../components/AIGeneration/AIGeneration.helper';
import AIGeneratorCommandService from '../../../services/studio/StudioCommand/AIGenerator/AIGeneratorCommand.services';
import { saveHangerCardsDraft, loadHangerCardsDraft, clearHangerCardsDraft } from './hangerCardsStore';
import type {
    ProductAttributeItem,
    ProductPomMeasurementSizeGroup,
} from '../../../services/query/productPomMeasurement/productPomMeasurement.types';

// AI Generation: Model selection type
export type ModelSelectionType = 'upload' | 'list';

// Photography type for AI Generation
export type PhotographyType = 'model' | 'hanger';

// AI Model Selection Type
export type AIModelSelectionType = 'EachLabs' | 'GoogleGemini' | 'AzureOpenAIGpt' | 'GoogleGeminiFlash' | 'GoogleGeminiNanoBanana21' | 'GoogleGeminiNanoBanana21Lcw' | 'AIGate' | 'GeminiProLcw' | 'AIGateProLcw';

// Hanger card interface for askılı çekim
export interface HangerCard {
    id: string;
    photos: PhotoFile[];  // Max 10 photos per card
    config: AIGenerationConfig;  // Each card has its own configuration
    selectedModel: AIModelSelectionType;  // Selected AI model for this card
    sourceImage?: {  // Source image for Gemini image editing
        preview: string;  // Base64 preview
        mimeType: string;  // MIME type (e.g., "image/jpeg")
    } | null;
    generationStatus: 'idle' | 'loading' | 'success' | 'error' | 'interrupted';
    predictionId?: string | null;  // Prediction ID for polling
    progress?: number;  // Progress percentage (0-100)
    pollingAttempts?: number;  // Number of polling attempts
    generatedResults: Array<{
        originalPhotoId: string;
        generatedUrls: string[];
        /** Üretim tamamlandığında kullanılan çıktı formatı (indirme için). */
        outputFormat?: string;
    }>;
    errorMessage?: string;
}

export interface MannequinData {
    id: number;
    name: string;
    chest: number;
    waist: number;
    hip: number;
    length: number;
    shoeSize: number;
    agencyName: string;
    photoUrl: string;
    passNo: string;
    active: boolean;
    isAdult: boolean;
    weight: number | null;
    lastShotDate: string | null;
    createdDate: string;
    updatedDate: string;
    isAi?: boolean;
}

export interface PhotoFile {
    id: string;
    file: File | null;
    url: string;
    preview: string;
    type?: 'model' | 'product';  // Distinguish between model and product photos
    name?: string;
    size?: number; // Size in bytes, can be populated from File or backend
}

export interface AIGenerationConfig {
    barcode: string;
    prefix: string;
    suffix: string;
    prompt: string;
    resolution: string;
    quality?: string;
    aspectRatio: string;
    outputFormat: string;
    outputCount: number;
    backgroundUrl?: string;
    backgroundName?: string;
    labelUrl?: string;
    labelName?: string;
    selectedSizeCode?: string;
    sizeMeasurementText?: string;
    sizeInfoSizes?: ProductPomMeasurementSizeGroup[];
    sizeInfoProductAttributes?: ProductAttributeItem[];
    sizeInfoLoaded?: boolean;
}

export type GenerationStatus = 'idle' | 'loading' | 'success' | 'error' | 'interrupted';

interface MannequinContextType {
    currentPage: number;
    pageSize: number;
    searchKeyword: string;
    mannequinListData: any;
    selectedMannequin: MannequinData | null;
    isLoading: boolean;
    searchTrigger: number;
    totalCount: number;
    setMannequinListData: (data: any) => void;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setSearchKeyword: (keyword: string) => void;
    setSelectedMannequin: (mannequin: MannequinData | null) => void;
    setIsLoading: (loading: boolean) => void;
    setSearchTrigger: (value: number | ((prev: number) => number)) => void;
    setTotalCount: (count: number) => void;
    handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // AI Generation states
    photographyType: PhotographyType | null;
    modelSelectionType: ModelSelectionType;
    aiSelectedMannequin: MannequinData | null;
    uploadedModelPhoto: PhotoFile | null;
    uploadedPhotos: PhotoFile[];
    aiConfig: AIGenerationConfig;
    generationStatus: GenerationStatus;
    predictionId: string | null;
    generatedPhotoUrls: string[];
    hangerCards: HangerCard[];
    isDraftLoading: boolean;
    setPhotographyType: (type: PhotographyType | null) => void;
    setModelSelectionType: (type: ModelSelectionType) => void;
    setAISelectedMannequin: (mannequin: MannequinData | null) => void;
    setUploadedModelPhoto: (photo: PhotoFile | null) => void;
    setUploadedPhotos: (photos: PhotoFile[]) => void;
    setAIConfig: (config: AIGenerationConfig) => void;
    setGenerationStatus: (status: GenerationStatus) => void;
    setPredictionId: (id: string | null) => void;
    setGeneratedPhotoUrls: (urls: string[]) => void;
    setHangerCards: (cards: SetStateAction<HangerCard[]>) => void;
    addHangerCard: () => void;
    duplicateHangerCard: (cardId: string) => void;
    duplicateHangerCardWithPhotos: (cardId: string) => void;
    removeHangerCard: (cardId: string) => void;
    updateHangerCard: (cardId: string, updates: Partial<HangerCard> | ((prevCard: HangerCard) => Partial<HangerCard>)) => void;
    resetAIGeneration: () => void;
    createCardFromHistory: (historyItem: AIProcessLogItem) => string;
    applyBackgroundOrLabelToCard: (cardId: string, url: string, name: string, type: 'background' | 'label', s3Key?: string) => Promise<void>;
}

interface MannequinProviderProps {
    children: ReactNode;
}

const MannequinContext = createContext<MannequinContextType | undefined>(undefined);

export const MannequinProvider: React.FC<MannequinProviderProps> = ({ children }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(16); // Fixed page size
    const [searchKeyword, setSearchKeyword] = useState('');
    const [mannequinListData, setMannequinListData] = useState<any>(null);
    const [selectedMannequin, setSelectedMannequin] = useState<MannequinData | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTrigger, setSearchTrigger] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    // AI Generation states
    const [photographyType, setPhotographyType] = useState<PhotographyType | null>(null);
    const [modelSelectionType, setModelSelectionType] = useState<ModelSelectionType>('upload');
    const [aiSelectedMannequin, setAISelectedMannequin] = useState<MannequinData | null>(null);
    const [uploadedModelPhoto, setUploadedModelPhoto] = useState<PhotoFile | null>(null);
    const [uploadedPhotos, setUploadedPhotos] = useState<PhotoFile[]>([]);
    const [aiConfig, setAIConfig] = useState<AIGenerationConfig>({
        barcode: '',
        prefix: '',
        suffix: '',
        prompt: '',
        resolution: '4K',
        aspectRatio: '3:4',
        outputFormat: 'jpeg',
        outputCount: 1,
    });
    const [generationStatus, setGenerationStatus] = useState<GenerationStatus>('idle');
    const [predictionId, setPredictionId] = useState<string | null>(null);
    const [generatedPhotoUrls, setGeneratedPhotoUrls] = useState<string[]>([]);
    const [hangerCards, setHangerCards] = useState<HangerCard[]>([]);
    const [isDraftLoading, setIsDraftLoading] = useState(true);

    // Load draft from IndexedDB on initial mount
    const draftLoadedRef = useRef(false);
    useEffect(() => {
        if (draftLoadedRef.current) return;
        draftLoadedRef.current = true;

        try { localStorage.removeItem('mannequin-hanger-cards-draft'); } catch { /* noop */ }

        loadHangerCardsDraft().then(cards => {
            if (cards.length > 0) setHangerCards(cards);
            setIsDraftLoading(false);
        });
    }, []);

    // Auto-save hanger cards to IndexedDB with debounce
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialLoadRef = useRef(true);
    useEffect(() => {
        if (isDraftLoading) return;
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            return;
        }
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

        const hasActiveGeneration = hangerCards.some(
            (c) => c.generationStatus === 'loading' || c.generationStatus === 'success' || c.generationStatus === 'interrupted'
        );
        const saveDelayMs = hasActiveGeneration ? 0 : 1000;

        saveTimerRef.current = setTimeout(() => {
            if (hangerCards.length > 0) {
                saveHangerCardsDraft(hangerCards);
            } else {
                clearHangerCardsDraft();
            }
        }, saveDelayMs);
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, [hangerCards, isDraftLoading]);

    // Sayfa yenilendiginde draft'in kaybolmamasi icin best-effort kayit
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (hangerCards.length > 0) {
                void saveHangerCardsDraft(hangerCards);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hangerCards]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
        setCurrentPage(1);
    };

    const addHangerCard = useCallback(() => {
        setHangerCards(prev => {
            if (prev.length >= 16) return prev;

            const { selectedModel, config } = resolveHangerCardModelSelection({
                cards: prev,
                intent: 'new',
                config: createEmptyHangerCardConfig(),
            });

            const newCard: HangerCard = {
                id: `card-${Date.now()}-${window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
                photos: [],
                config,
                selectedModel,
                generationStatus: 'idle',
                generatedResults: [],
            };
            return [...prev, newCard];
        });
    }, []);

    const duplicateHangerCard = useCallback((cardId: string) => {
        setHangerCards(prev => {
            const cardToDuplicate = prev.find(card => card.id === cardId);
            if (!cardToDuplicate || prev.length >= 16) return prev;

            const { selectedModel, config } = resolveHangerCardModelSelection({
                cards: prev,
                intent: 'duplicate',
                requestedModel: cardToDuplicate.selectedModel,
                config: {
                    ...cardToDuplicate.config,
                    barcode: resolveDuplicateBarcode(
                        cardToDuplicate.config.barcode,
                        prev.map((card) => card.config.barcode)
                    ),
                },
            });

            const newCard: HangerCard = {
                id: `card-${Date.now()}-${window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
                photos: [],
                config,
                selectedModel,
                generationStatus: 'idle',
                generatedResults: [],
            };
            return [...prev, newCard];
        });
    }, []);

    const duplicateHangerCardWithPhotos = useCallback((cardId: string) => {
        setHangerCards(prev => {
            const cardToDuplicate = prev.find(card => card.id === cardId);
            if (!cardToDuplicate || prev.length >= 16) return prev;

            const { selectedModel, config } = resolveHangerCardModelSelection({
                cards: prev,
                intent: 'duplicate',
                requestedModel: cardToDuplicate.selectedModel,
                config: {
                    ...cardToDuplicate.config,
                    barcode: resolveDuplicateBarcode(
                        cardToDuplicate.config.barcode,
                        prev.map((card) => card.config.barcode)
                    ),
                },
            });

            const newCard: HangerCard = {
                id: `card-${Date.now()}-${window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
                photos: [...cardToDuplicate.photos],
                config,
                selectedModel,
                generationStatus: 'idle',
                generatedResults: [],
            };
            return [...prev, newCard];
        });
    }, []);

    const createCardFromHistory = (historyItem: AIProcessLogItem): string => {
        if (hangerCards.length >= 16) return '';

        // Parse image URLs from JSON string
        const imageUrls = parseImageUrls(historyItem.imageUrls);
        
        console.log('📋 Creating card from history:', {
            id: historyItem.id,
            barcode: historyItem.productBarcode,
            imageUrlsCount: imageUrls.length,
            imageUrlsSample: imageUrls.map(u => u.substring(0, 80) + '...'),
        });
        
        // Create PhotoFile objects from URLs (initial - will be updated with base64)
        const photos = createPhotoFilesFromHistory(imageUrls);

        const newCardId = `card-${Date.now()}-${window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
        
        const newCard: HangerCard = {
            id: newCardId,
            photos: photos,
            config: {
                barcode: historyItem.productBarcode || '',
                prefix: '',
                suffix: '',
                prompt: historyItem.prompt || '',
                resolution: historyItem.resolution || '4K',
                aspectRatio: historyItem.aspectRatio || '3:4',
                outputFormat: historyItem.outputFormat || 'jpeg',
                outputCount: historyItem.numImages || 1,
            },
            selectedModel: 'GoogleGemini',  // Default model for history items
            generationStatus: 'idle',
            generatedResults: [],
        };
        
        setHangerCards(prev => [...prev, newCard]);

        // Async: fetch each image through BFF proxy and update card photos with base64
        // This handles S3 URLs that have CORS restrictions or require authentication
        if (imageUrls.length > 0) {
            const fetchPhotosAsBase64 = async () => {
                const updatedPhotos: PhotoFile[] = [];
                
                for (let i = 0; i < imageUrls.length; i++) {
                    const url = imageUrls[i];
                    const photo = photos[i];
                    
                    // Skip if already base64
                    if (url.startsWith('data:')) {
                        updatedPhotos.push(photo);
                        continue;
                    }
                    
                    try {
                        const base64DataUrl = await AIGeneratorCommandService.FetchImageAsBase64(url);
                        updatedPhotos.push({
                            ...photo,
                            preview: base64DataUrl,
                        });
                        console.log(`✅ Photo ${i + 1}/${imageUrls.length} fetched as base64`);
                    } catch (error) {
                        console.warn(`⚠️ Failed to fetch photo ${i + 1} via BFF proxy, using original URL:`, error);
                        updatedPhotos.push(photo); // Keep original URL as fallback
                    }
                }
                
                // Update card with base64 photos
                setHangerCards(prev => prev.map(card => 
                    card.id === newCardId ? { ...card, photos: updatedPhotos } : card
                ));
                console.log(`📸 All ${updatedPhotos.length} photos loaded for history card`);
            };
            
            fetchPhotosAsBase64();
        }

        return newCardId;
    };

    const removeHangerCard = useCallback((cardId: string) => {
        setHangerCards(prev => prev.filter(card => card.id !== cardId));
    }, []);

    const updateHangerCard = useCallback((cardId: string, updates: Partial<HangerCard> | ((prevCard: HangerCard) => Partial<HangerCard>)) => {
        setHangerCards(prevCards => prevCards.map(card => {
            if (card.id !== cardId) return card;
            
            const updatesToApply = typeof updates === 'function' ? updates(card) : updates;
            return { ...card, ...updatesToApply };
        }));
    }, []);

    const applyBackgroundOrLabelToCard = useCallback(async (cardId: string, url: string, name: string, type: 'background' | 'label', s3Key?: string) => {
        // S3'ten görseli BFF proxy üzerinden indirip base64'e çevir (kullanıcı fotoğrafları gibi)
        let base64Preview = url; // Fallback olarak URL kullan
        try {
            if (s3Key) {
                base64Preview = await fetchImageAsBase64(s3Key);
            }
        } catch (error) {
            console.error(`Failed to convert ${type} image to base64, using URL as fallback:`, error);
        }

        updateHangerCard(cardId, (prevCard) => {
            const newPhoto: PhotoFile = {
                id: `${type}-${Date.now()}-${window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
                file: null,
                url: url,
                preview: base64Preview,
                type: 'product',
                name: name || `${type}-image`,
            };

            const configUpdate = type === 'background'
                ? { backgroundUrl: url, backgroundName: name }
                : { labelUrl: url, labelName: name };

            return {
                photos: [...prevCard.photos, newPhoto],
                config: { ...prevCard.config, ...configUpdate },
            };
        });
    }, [updateHangerCard]);

    const resetAIGeneration = () => {
        setPhotographyType(null);
        setModelSelectionType('upload');
        setAISelectedMannequin(null);
        setUploadedModelPhoto(null);
        setUploadedPhotos([]);
        setAIConfig({
            barcode: '',
            prefix: '',
            suffix: '',
            prompt: '',
            resolution: '4K',
            aspectRatio: '3:4',
            outputFormat: 'jpeg',
            outputCount: 1,
        });
        setGenerationStatus('idle');
        setPredictionId(null);
        setGeneratedPhotoUrls([]);
        setHangerCards([]);
        clearHangerCardsDraft();
    };

    const value = useMemo(
        () => ({
            currentPage,
            pageSize,
            searchKeyword,
            mannequinListData,
            selectedMannequin,
            isLoading,
            searchTrigger,
            totalCount,
            setMannequinListData,
            setCurrentPage,
            setPageSize,
            setSearchKeyword,
            setSelectedMannequin,
            setIsLoading,
            setSearchTrigger,
            setTotalCount,
            handleSearch,
            // AI Generation
            photographyType,
            modelSelectionType,
            aiSelectedMannequin,
            uploadedModelPhoto,
            uploadedPhotos,
            aiConfig,
            generationStatus,
            predictionId,
            generatedPhotoUrls,
            hangerCards,
            isDraftLoading,
            setPhotographyType,
            setModelSelectionType,
            setAISelectedMannequin,
            setUploadedModelPhoto,
            setUploadedPhotos,
            setAIConfig,
            setGenerationStatus,
            setPredictionId,
            setGeneratedPhotoUrls,
            setHangerCards,
            addHangerCard,
            duplicateHangerCard,
            duplicateHangerCardWithPhotos,
            removeHangerCard,
            updateHangerCard,
            resetAIGeneration,
            createCardFromHistory,
            applyBackgroundOrLabelToCard,
        }),
        [
            currentPage,
            pageSize,
            searchKeyword,
            mannequinListData,
            selectedMannequin,
            isLoading,
            searchTrigger,
            totalCount,
            photographyType,
            modelSelectionType,
            aiSelectedMannequin,
            uploadedModelPhoto,
            uploadedPhotos,
            aiConfig,
            generationStatus,
            predictionId,
            generatedPhotoUrls,
            hangerCards,
            isDraftLoading,
        ]
    );

    return <MannequinContext.Provider value={value}>{children}</MannequinContext.Provider>;
};

export const useMannequin = () => {
    const context = useContext(MannequinContext);
    if (context === undefined) {
        throw new Error('useMannequin must be used within a MannequinProvider');
    }
    return context;
};

// Alias for AI generation components
export const useMannequinContext = useMannequin;

